/**
 * In-memory cache for one brand's Shopify variant lookup table.
 *
 * Why:
 * After we fetch Honda's map from Dato, we keep it here so submit does not
 * need to refetch if the make has not changed.
 *
 * Updated by: fetchShopifyVariantsByMake
 * Read by: form submit paths (or they call fetch again, which hits this cache)
 */

import type { UpholsteryShopifyVariantEntry } from "@views/Upholstery/types"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

export interface ShopifyVariantsByMakeState {
  /** Last loaded make slug, e.g. "honda". */
  makeKey: string | null
  /** Dato record key, e.g. "honda-shopify-variants". */
  recordKey: string | null
  /** Clean rows from options.shopifyVariants. */
  entries: UpholsteryShopifyVariantEntry[]
  loading: boolean
  error: string | null
  /** Timestamp of last successful setEntries call. */
  fetchedAt: number | null
  actions: {
    /** Replace the cached brand map after a fetch. */
    setEntries: (params: {
      makeKey: string
      recordKey: string
      entries: UpholsteryShopifyVariantEntry[]
    }) => void
    setLoading: (loading: boolean) => void
    setError: (error: string | null) => void
    /** Clear cache (e.g. empty make). */
    reset: () => void
  }
}

const initialState = {
  makeKey: null as string | null,
  recordKey: null as string | null,
  entries: [] as UpholsteryShopifyVariantEntry[],
  loading: false,
  error: null as string | null,
  fetchedAt: null as number | null,
}

export const useShopifyVariantsByMakeStore =
  create<ShopifyVariantsByMakeState>()(
    devtools(
      (set) => ({
        ...initialState,
        actions: {
          setEntries: ({ makeKey, recordKey, entries }) =>
            set({
              makeKey,
              recordKey,
              entries,
              loading: false,
              error: null,
              fetchedAt: Date.now(),
            }),
          setLoading: (loading) => set({ loading }),
          setError: (error) => set({ error, loading: false }),
          reset: () => set(initialState),
        },
      }),
      { name: "ShopifyVariantsByMakeStore" }
    )
  )
